import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  getScenarios, createScenario, getScenario, updateScenario, deleteScenario,
  addScenarioItem, deleteScenarioItem, getScenarioProjection,
} from '../../api/client';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthKeyLabel = (key) => { const [y, m] = String(key).split('-'); return `${MONTHS[+m - 1]} ${y}`; };

// ─── Scenario detail (projection + items) ────────────────────────────────────
const ScenarioDetail = ({ id, onBack }) => {
  const { COLORS } = useTheme();
  const [scenario, setScenario]     = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [lens, setLens]             = useState('year');   // 'year' | 'month'
  const [focusKey, setFocusKey]     = useState(null);
  const [savingVar, setSavingVar]   = useState(false);
  const [itemKind, setItemKind]     = useState('expense');
  const [itemLabel, setItemLabel]   = useState('');
  const [itemAmount, setItemAmount] = useState('');
  const [itemMonth, setItemMonth]   = useState('');
  const [adding, setAdding]         = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        getScenario(id),
        getScenarioProjection(id, { months: 12 }),
      ]);
      setScenario(sRes.data);
      setProjection(pRes.data);
      const firstKey = pRes.data.periods?.[0]?.key;
      if (firstKey) {
        setFocusKey((prev) => prev || firstKey);
        setItemMonth((prev) => prev || firstKey);
      }
    } catch (e) { /* handled by empty UI */ }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refreshProjection = async () => {
    try { const p = await getScenarioProjection(id, { months: 12 }); setProjection(p.data); } catch (e) {}
  };

  const toggleVariable = async () => {
    setSavingVar(true);
    try {
      const r = await updateScenario(id, { include_variable: !scenario.include_variable });
      setScenario((prev) => ({ ...prev, include_variable: r.data.include_variable }));
      await refreshProjection();
    } catch (e) {} finally { setSavingVar(false); }
  };

  const handleAddItem = async () => {
    if (!itemLabel.trim() || !itemMonth) return;
    setAdding(true);
    try {
      await addScenarioItem(id, { kind: itemKind, label: itemLabel.trim(), amount: parseFloat(itemAmount || 0), month: itemMonth });
      setItemLabel(''); setItemAmount('');
      await load();
    } catch (e) {} finally { setAdding(false); }
  };

  const handleDeleteItem = async (itemId) => {
    try { await deleteScenarioItem(id, itemId); await load(); } catch (e) {}
  };

  const handleDeleteScenario = async () => {
    if (!window.confirm('Delete this plan and all its items?')) return;
    try { await deleteScenario(id); onBack(); } catch (e) {}
  };

  if (loading || !scenario || !projection) {
    return <div style={{ padding: 24, color: COLORS.textMuted, fontFamily: "'DM Sans', sans-serif" }}>Loading…</div>;
  }

  const inp = { padding: '9px 10px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
  const netColor = (n) => (n < 0 ? COLORS.red : COLORS.green);
  const Row = ({ label, value, color, bold }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
      <span style={{ color: COLORS.textMuted, fontSize: 13 }}>{label}</span>
      <span style={{ color, fontFamily: 'monospace', fontSize: bold ? 16 : 13, fontWeight: bold ? 800 : 600 }}>{value}</span>
    </div>
  );

  const periods    = projection.periods || [];
  const focus      = periods.find((p) => p.key === focusKey) || periods[0];
  const focusIndex = periods.findIndex((p) => p.key === focus?.key);
  const items      = scenario.items || [];
  const shortfall  = focus && focus.net < 0 ? Math.abs(focus.net) : 0;
  const perMonth   = shortfall && focusIndex > 0 ? shortfall / focusIndex : 0;

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 10, padding: 0 }}>‹ All plans</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>{scenario.name}</h2>
        <button onClick={handleDeleteScenario} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.red, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Delete plan</button>
      </div>

      {/* controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, background: COLORS.inputBg, padding: 3, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
          {['year', 'month'].map((l) => (
            <button key={l} onClick={() => setLens(l)} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, textTransform: 'capitalize', background: lens === l ? COLORS.accent : 'transparent', color: lens === l ? '#fff' : COLORS.textMuted }}>{l}</button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: COLORS.text, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={scenario.include_variable} onChange={toggleVariable} disabled={savingVar} />
          Include variable spending
        </label>
      </div>

      {/* YEAR LENS */}
      {lens === 'year' && (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 22 }}>
          {periods.map((p) => (
            <button key={p.key} onClick={() => { setFocusKey(p.key); setLens('month'); }} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: `1px solid ${COLORS.border}`, padding: '11px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 }}>
              <span style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>
                {p.label}{p.itemCount > 0 && <span style={{ color: COLORS.accent, fontSize: 11, fontWeight: 700 }}> · {p.itemCount} one-off{p.itemCount === 1 ? '' : 's'}</span>}
              </span>
              <span style={{ color: netColor(p.net), fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{fmt(p.net)}</span>
            </button>
          ))}
          <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, background: COLORS.inputBg }}>
            <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>12-month net</span>
            <span style={{ color: netColor(projection.totals.net), fontFamily: 'monospace', fontSize: 15, fontWeight: 800 }}>{fmt(projection.totals.net)}</span>
          </div>
        </div>
      )}

      {/* MONTH LENS */}
      {lens === 'month' && focus && (
        <div style={{ marginBottom: 22 }}>
          <select value={focus.key} onChange={(e) => setFocusKey(e.target.value)} style={{ width: '100%', ...inp, padding: '10px 12px', fontSize: 14, marginBottom: 14 }}>
            {periods.map((p) => <option key={p.key} value={p.key}>{p.label}  ({p.start} – {p.end})</option>)}
          </select>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '16px 18px' }}>
            <Row label="Expected income" value={fmt(focus.expectedIncome)} color={COLORS.text} />
            {focus.scenarioIncome > 0 && <Row label="+ Planned income" value={fmt(focus.scenarioIncome)} color={COLORS.green} />}
            <Row label="Expected expenses" value={fmt(focus.expectedExpenses)} color={COLORS.text} />
            {focus.scenarioExpenses > 0 && <Row label="+ Planned one-offs" value={fmt(focus.scenarioExpenses)} color={COLORS.amber} />}
            <div style={{ height: 1, background: COLORS.border, margin: '10px 0' }} />
            <Row label="Projected net" value={fmt(focus.net)} color={netColor(focus.net)} bold />
          </div>
          {focus.net < 0 && focusIndex > 0 && (
            <div style={{ background: COLORS.amberGlow || COLORS.redGlow, border: `1px solid ${COLORS.amber}44`, borderRadius: 12, padding: '12px 16px', marginTop: 12, color: COLORS.text, fontSize: 13, lineHeight: 1.5 }}>
              To cover the <b>{fmt(shortfall)}</b> shortfall in {focus.label}, set aside <b>{fmt(perMonth)}/month</b> for the next {focusIndex} month{focusIndex === 1 ? '' : 's'}.
            </div>
          )}
        </div>
      )}

      {/* ITEMS */}
      <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Planned one-offs</h3>
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={itemKind} onChange={(e) => setItemKind(e.target.value)} style={{ ...inp, flex: 1 }}>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
          <input type="month" value={itemMonth} onChange={(e) => setItemMonth(e.target.value)} style={{ ...inp, flex: 1 }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={itemLabel} onChange={(e) => setItemLabel(e.target.value)} placeholder="Label (e.g. Cancún flights)" style={{ ...inp, flex: 2 }} />
          <input value={itemAmount} onChange={(e) => setItemAmount(e.target.value)} placeholder="Amount" type="number" style={{ ...inp, flex: 1 }} />
        </div>
        <button onClick={handleAddItem} disabled={adding || !itemLabel.trim() || !itemMonth} style={{ background: COLORS.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: adding || !itemLabel.trim() || !itemMonth ? 0.6 : 1 }}>Add to plan</button>
      </div>
      {items.length === 0 ? (
        <p style={{ color: COLORS.textMuted, fontSize: 13 }}>No one-offs yet — add a vacation, a big bill, or extra income above.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((it) => (
            <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px' }}>
              <div>
                <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{it.label}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{monthKeyLabel(it.month_key)} · {it.kind}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: it.kind === 'income' ? COLORS.green : COLORS.amber, fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{it.kind === 'income' ? '+' : ''}{fmt(parseFloat(it.amount))}</span>
                <button onClick={() => handleDeleteItem(it.id)} aria-label="Delete" style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Scenario list ───────────────────────────────────────────────────────────
const Planning = () => {
  const { COLORS } = useTheme();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [newName, setNewName]     = useState('');
  const [creating, setCreating]   = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { const r = await getScenarios(); setScenarios(r.data || []); } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await createScenario({ name: newName.trim() });
      setNewName('');
      await loadList();
      setSelectedId(r.data.id);
    } catch (e) {} finally { setCreating(false); }
  };

  if (selectedId) {
    return <ScenarioDetail id={selectedId} onBack={() => { setSelectedId(null); loadList(); }} />;
  }

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <h2 style={{ color: COLORS.text, fontSize: 24, fontWeight: 800, margin: '0 0 4px', letterSpacing: -0.5 }}>Planning</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 14, margin: '0 0 20px' }}>Save "what-if" plans for upcoming months — vacations, big expenses, extra income.</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="New plan name (e.g. December – Cancún)"
          style={{ flex: 1, padding: '10px 14px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
        />
        <button onClick={handleCreate} disabled={creating || !newName.trim()} style={{ background: COLORS.accent, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: creating || !newName.trim() ? 0.6 : 1 }}>+ New plan</button>
      </div>

      {loading ? (
        <p style={{ color: COLORS.textMuted }}>Loading…</p>
      ) : scenarios.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🗓️</div>
          No plans yet. Create one above to start forecasting.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => setSelectedId(s.id)} style={{ textAlign: 'left', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: COLORS.text, fontSize: 15, fontWeight: 700 }}>{s.name}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 3 }}>
                  {s.item_count} one-off{s.item_count === 1 ? '' : 's'} · variable {s.include_variable ? 'on' : 'off'}
                </div>
              </div>
              <span style={{ color: COLORS.textMuted, fontSize: 20 }}>›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Planning;
